import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { EntityDefinition } from '../../types/entity-definition.interface';
import { EntityFormDeleteAction, EntityFormValue } from '../../types/entity-form-result.interface';
import { AssetGroup } from '../../types/asset-group.interface';
import { GenericEntityFormComponent } from '../generic/generic-entity-form.component';

@Component({
  selector: 'app-asset-group-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonModule, InputTextModule, TextareaModule, GenericEntityFormComponent],
  templateUrl: './asset-group-form.component.html',
})
export class AssetGroupFormComponent implements OnInit {
  @Input() entityDefinition?: EntityDefinition<AssetGroup>;
  @Input() entity?: AssetGroup;
  @Input() parentId?: number;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() additionalData?: Record<string, unknown>;

  @Output() formSubmit = new EventEmitter<EntityFormValue<AssetGroup>>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() deleteEntity = new EventEmitter<AssetGroup>();

  private readonly config = inject<DynamicDialogConfig<EntityDialogData<AssetGroup>> | null>(DynamicDialogConfig, {
    optional: true,
  });
  private readonly dialogRef = inject<DynamicDialogRef<AssetGroupFormComponent> | null>(DynamicDialogRef, {
    optional: true,
  });

  private directDeleteCallback?: (entity: AssetGroup) => void;

  ngOnInit(): void {
    const data = this.config?.data;
    if (!data) {
      return;
    }
    this.entityDefinition = data.entityDefinition;
    this.mode = data.mode === 'edit' ? 'edit' : 'create';
    if (data.entity !== undefined) this.entity = data.entity;
    if (data.parentId !== undefined) this.parentId = data.parentId;
    if (data.additionalData !== undefined) this.additionalData = data.additionalData;
    if (data.onDelete !== undefined) this.directDeleteCallback = data.onDelete;
  }

  onFormSubmit(formData: EntityFormValue<AssetGroup>): void {
    if (this.dialogRef) {
      this.dialogRef.close(formData);
    } else {
      this.formSubmit.emit(formData);
    }
  }

  onFormCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.formCancel.emit();
    }
  }

  onDeleteRequest(entity: AssetGroup): void {
    if (this.directDeleteCallback) {
      this.directDeleteCallback(entity);
      this.dialogRef?.close();
      return;
    }
    if (this.dialogRef) {
      const deleteAction: EntityFormDeleteAction<AssetGroup> = { __action: 'delete', entity };
      this.dialogRef.close(deleteAction);
    } else {
      this.deleteEntity.emit(entity);
    }
  }
}
